import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Modal } from '@tinper/next-ui';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { formatResourceCount, IconBadge } from './MarketplaceShared';
const FRONTMATTER_RE = /^\s*---\s*\n[\s\S]*?\n---\s*(?:\n|$)/;
const CRITICAL_BLOCK_RE = /<critical_system_prerequisite[^>]*>[\s\S]*?<\/critical_system_prerequisite>/gi;
const contentGroupLabels = {
    workflows: 'AutoPilot 自动化流程',
    agents: 'Agent 智能体',
    skills: 'Skills 技能',
    tools: 'Tools 工具',
};
function isPluginItem(item) {
    return 'packageName' in item;
}
function uniqueText(values) {
    return Array.from(new Set(values.map((value) => value?.trim()).filter(Boolean)));
}
function formatResourceVersion(value) {
    if (!value)
        return '';
    return value.toLowerCase().startsWith('v') ? value : `v${value}`;
}
function getLatestVersion(item) {
    return isPluginItem(item)
        ? item.version || item.versions[0]?.version
        : item.versions[0]?.version;
}
function getTenantLabel(item, kind) {
    if (kind === 'plugins') {
        const plugin = item;
        if (plugin.tenantKey === '0')
            return '官方插件';
        return '租户插件';
    }
    const skill = item;
    if (skill.tenantKey === '0')
        return '官方技能';
    return '租户技能';
}
function getSourceLabel(item, kind) {
    if (kind === 'plugins') {
        const plugin = item;
        return plugin.sourceLabels[0] || getTenantLabel(plugin, kind);
    }
    const skill = item;
    return getTenantLabel(skill, kind);
}
function getTitle(item) {
    return isPluginItem(item) ? item.name : item.title;
}
function getCodeLine(item) {
    if (isPluginItem(item))
        return item.packageName || item.name;
    return item.name || item.title;
}
function getPrimaryCategory(item, kind) {
    if (kind === 'plugins')
        return item.typeLabels[0] || '插件';
    return item.categories[0] || '技能';
}
function buildTags(item, kind) {
    const version = formatResourceVersion(getLatestVersion(item));
    if (kind === 'plugins') {
        const plugin = item;
        return uniqueText([version, getTenantLabel(plugin, kind), plugin.typeLabels[0]]);
    }
    const skill = item;
    return uniqueText([version, getTenantLabel(skill, kind), skill.categories[0], skill.productLines[0]]);
}
function getTagToneClass(tag) {
    if (tag.includes('官方'))
        return 'resource-tag--official';
    if (tag.includes('租户'))
        return 'resource-tag--tenant';
    return '';
}
function buildPluginContentGroups(plugin) {
    return [
        { key: 'workflows', label: contentGroupLabels.workflows, items: plugin.workflows },
        { key: 'agents', label: contentGroupLabels.agents, items: plugin.agents },
        { key: 'skills', label: contentGroupLabels.skills, items: plugin.skills },
        { key: 'tools', label: contentGroupLabels.tools, items: plugin.tools },
    ].filter((group) => group.items.length);
}
function buildVersionItems(item) {
    const latestVersion = getLatestVersion(item);
    if (item.versions.length)
        return item.versions;
    if (!latestVersion)
        return [];
    return [
        {
            version: latestVersion,
            updatedAt: item.updatedAt,
            sourceCodeUrl: item.documentUrl,
            changeLog: item.changeLog,
        },
    ];
}
function cleanResourceDocument(markdown) {
    return markdown
        .replace(/^\uFEFF/, '')
        .replace(FRONTMATTER_RE, '')
        .replace(CRITICAL_BLOCK_RE, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
function resolveDocumentAssetUrl(url, baseUrl) {
    if (!url || !baseUrl || url.startsWith('http') || url.startsWith('/') || url.startsWith('#')) {
        return url;
    }
    try {
        return new URL(url, baseUrl).toString();
    }
    catch {
        return url;
    }
}
function createResourceDocumentComponents(baseUrl) {
    return {
        a: ({ href, children, node: _node, ...props }) => {
            const resolvedHref = resolveDocumentAssetUrl(href, baseUrl);
            return (_jsx("a", { href: resolvedHref, target: "_blank", rel: "noopener noreferrer", ...props, children: children }));
        },
        img: ({ src, alt, node: _node, ...props }) => {
            return _jsx("img", { src: resolveDocumentAssetUrl(src, baseUrl), alt: alt || '', ...props });
        },
    };
}
export function ResourceDetailModal({ kind, item, visible, onClose }) {
    const [activeTab, setActiveTab] = React.useState('overview');
    const [documentMarkdown, setDocumentMarkdown] = React.useState('');
    const [documentLoading, setDocumentLoading] = React.useState(false);
    const [documentError, setDocumentError] = React.useState('');
    React.useEffect(() => {
        if (visible)
            setActiveTab('overview');
    }, [visible, item?.id]);
    React.useEffect(() => {
        if (!visible || !item?.documentUrl) {
            setDocumentMarkdown('');
            setDocumentError('');
            setDocumentLoading(false);
            return;
        }
        const controller = new AbortController();
        let active = true;
        setDocumentMarkdown('');
        setDocumentError('');
        setDocumentLoading(true);
        fetch(item.documentUrl, { signal: controller.signal })
            .then((response) => {
            if (!response.ok) {
                throw new Error(`Document request failed: ${response.status}`);
            }
            return response.text();
        })
            .then((markdown) => {
            if (!active)
                return;
            setDocumentMarkdown(cleanResourceDocument(markdown));
            setDocumentLoading(false);
        })
            .catch((error) => {
            if (!active || error.name === 'AbortError')
                return;
            console.error(error);
            setDocumentError('文档暂时无法加载');
            setDocumentLoading(false);
        });
        return () => {
            active = false;
            controller.abort();
        };
    }, [visible, item?.id, item?.documentUrl]);
    const documentComponents = React.useMemo(() => createResourceDocumentComponents(item?.documentUrl), [item?.documentUrl]);
    if (!item)
        return null;
    const isPlugin = kind === 'plugins';
    const title = getTitle(item);
    const codeLine = getCodeLine(item);
    const latestVersion = formatResourceVersion(getLatestVersion(item));
    const sourceLabel = getSourceLabel(item, kind);
    const tenantLabel = getTenantLabel(item, kind);
    const primaryCategory = getPrimaryCategory(item, kind);
    const tabs = isPlugin
        ? [
            { label: '概述', value: 'overview' },
            { label: '内容清单', value: 'content' },
            { label: '版本历史', value: 'history' },
        ]
        : [
            { label: '概述', value: 'overview' },
            { label: '版本历史', value: 'history' },
        ];
    const resolvedActiveTab = tabs.some((tab) => tab.value === activeTab) ? activeTab : 'overview';
    const stats = isPlugin
        ? [
            ['版本', latestVersion || '-'],
            ['访问量', formatResourceCount(item.views)],
            ['分类', primaryCategory],
        ]
        : [
            ['版本', latestVersion || '-'],
            ['访问量', formatResourceCount(item.views)],
            ['启用次数', formatResourceCount(item.activations)],
        ];
    const versionItems = buildVersionItems(item);
    return (_jsx(Modal, { visible: visible, header: null, footer: null, closable: false, mask: true, maskClosable: false, maskClassName: "resource-detail-modal-mask", wrapClassName: "resource-detail-modal", className: "resource-detail-modal-dialog", bodyClassName: "resource-detail-modal-body", width: 880, centered: true, destroyOnClose: true, autoFocus: ".resource-modal-close", onCancel: onClose, onMaskClick: onClose, onEscapeKeyUp: onClose, children: _jsxs("section", { className: "resource-modal-card", "data-resource-kind": kind, "aria-labelledby": "resource-modal-title", children: [_jsxs("header", { className: "resource-modal-header", children: [_jsxs("div", { className: "resource-modal-titlebar", children: [_jsx("strong", { className: "resource-modal-kind", children: isPlugin ? '插件详情' : '技能详情' }), _jsx("button", { className: "resource-modal-close", type: "button", "aria-label": "\u5173\u95ED\u8BE6\u60C5", onClick: onClose, children: "\u00D7" })] }), _jsxs("div", { className: "resource-modal-summary-row", children: [_jsxs("div", { className: "resource-modal-identity", children: [_jsx(IconBadge, { icon: isPlugin ? 'plugin' : item.icon, color: item.color, label: title, imageSrc: item.iconUrl, variant: "resource", className: "resource-modal-icon" }), _jsxs("div", { className: "resource-modal-title-block", children: [_jsxs("div", { className: "resource-modal-title-main", children: [_jsx("h2", { id: "resource-modal-title", children: title }), latestVersion && _jsx("em", { className: "resource-modal-version", children: latestVersion })] }), _jsx("span", { className: "resource-modal-code", children: codeLine }), _jsx("p", { className: "resource-modal-desc", children: item.description || '暂无描述。' }), _jsx("div", { className: "resource-modal-tags", children: buildTags(item, kind).map((tag) => (_jsx("span", { className: ['resource-tag', getTagToneClass(tag)].filter(Boolean).join(' '), children: tag }, tag))) }), _jsxs("div", { className: "resource-modal-source", children: [_jsxs("span", { children: ["\u6765\u6E90\uFF1A", sourceLabel] }), _jsxs("span", { children: ["\u66F4\u65B0\u65F6\u95F4\uFF1A", item.updatedAt || '-'] })] })] })] }), _jsxs("aside", { className: "resource-modal-stats", "aria-label": "\u8D44\u6E90\u7EDF\u8BA1", children: [_jsx("span", { className: "resource-modal-status", children: tenantLabel }), _jsx("div", { className: "resource-modal-stats-list", children: stats.map(([label, value]) => (_jsxs("span", { children: [label, "\uFF1A", value] }, label))) })] })] })] }), _jsx("nav", { className: "resource-modal-tabs", role: "tablist", "aria-label": "\u8D44\u6E90\u8BE6\u60C5\u9875\u7B7E", children: tabs.map((tab) => (_jsx("button", { className: "resource-modal-tab", type: "button", role: "tab", "aria-selected": resolvedActiveTab === tab.value, onClick: () => setActiveTab(tab.value), children: tab.label }, tab.value))) }), _jsxs("div", { className: "resource-modal-body", children: [resolvedActiveTab === 'overview' && (_jsxs("article", { className: "resource-modal-document", role: "tabpanel", children: [documentLoading && _jsx("div", { className: "skill-document-state", children: "\u52A0\u8F7D\u6587\u6863\u4E2D..." }), !documentLoading && documentError && _jsx("div", { className: "skill-document-state", children: documentError }), !documentLoading && !documentError && documentMarkdown && (_jsx("div", { className: "skill-markdown", children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: documentComponents, children: documentMarkdown }) })), !documentLoading && !documentError && !documentMarkdown && (_jsx("div", { className: "skill-document-state", children: "\u6682\u65E0\u6982\u8FF0\u5185\u5BB9" }))] })), resolvedActiveTab === 'content' && isPlugin && (_jsx(PluginContentPanel, { groups: buildPluginContentGroups(item) })), resolvedActiveTab === 'history' && (_jsx(VersionHistoryPanel, { items: versionItems, documentUrl: item.documentUrl }))] })] }) }));
}
function PluginContentPanel({ groups }) {
    if (!groups.length) {
        return _jsx("span", { className: "resource-modal-empty", children: "\u6682\u65E0\u63D2\u4EF6\u5185\u5BB9\u6E05\u5355" });
    }
    return (_jsx("div", { className: "resource-content-list", children: groups.map((group) => (_jsxs("section", { className: "resource-content-group", children: [_jsx("strong", { children: group.label }), _jsx("div", { className: "resource-content-table-wrap", children: _jsxs("table", { children: [_jsxs("colgroup", { children: [_jsx("col", { style: { width: 132 } }), _jsx("col", { style: { width: 160 } }), _jsx("col", {})] }), _jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "\u7F16\u7801" }), _jsx("th", { children: "\u540D\u79F0" }), _jsx("th", { children: "\u63CF\u8FF0" })] }) }), _jsx("tbody", { children: group.items.map((entry) => (_jsxs("tr", { children: [_jsx("td", { children: _jsx("code", { children: entry.code || '-' }) }), _jsx("td", { children: _jsx("strong", { children: entry.name || entry.code || '-' }) }), _jsx("td", { children: entry.description || '暂无描述' })] }, `${group.key}-${entry.code || entry.name}`))) })] }) })] }, group.key))) }));
}
function VersionHistoryPanel({ items, documentUrl }) {
    if (!items.length) {
        return _jsx("span", { className: "resource-modal-empty", children: "\u6682\u65E0\u7248\u672C\u5386\u53F2" });
    }
    return (_jsx("ol", { className: "resource-version-list", children: items.slice(0, 8).map((versionItem, index) => (_jsxs("li", { className: "resource-version-item", children: [_jsx("i", { "aria-hidden": "true" }), _jsxs("div", { children: [_jsxs("strong", { children: [formatResourceVersion(versionItem.version) || '-', index === 0 && _jsx("em", { children: "\u6700\u65B0" })] }), _jsx("span", { children: versionItem.changeLog || (versionItem.sourceCodeUrl || documentUrl ? 'Synced by skillhub pipeline' : '暂无更新说明') })] }), _jsx("time", { children: versionItem.updatedAt || '-' })] }, `${versionItem.version}-${versionItem.updatedAt || index}`))) }));
}
